import React from 'react'
import type { BrowserProductQueryQuery } from '@generated/graphql'
import {
  getCategorie,
  getItemSizeFromProperties,
} from 'src/utils/getProductDetailsInfo'
import type { CurrencyCode } from '@faststore/sdk'
import { useSession } from 'src/sdk/session'
import { getCookie } from 'src/utils/cookie'
import { useWishlistContext } from 'src/contexts/WishlistContext/wishlist-context'
import { useStampsDataContext } from 'src/contexts/stamps-data-context'
import { useProductAbTestEvent } from 'src/sdk/analytics/hooks/useProductAbTestEvent'
import { useSelectedSKU } from 'src/contexts/ProductContext/useProductContext'
import { useRemoveFromWishlist } from 'src/sdk/analytics/hooks/useRemoveFromWishlistEvent'
import AddToWishlistModal from 'src/components/modal/AddToWishlistModal'
import SingleInputModal from 'src/components/modal/SingleInputModal'
import Breadcrumb from 'src/components/ui/Breadcrumb'
import StampsProduct from 'src/components/common/StampsProduct'
import WishlistHeart from 'src/components/ui/WishlistHeart'
import { ImageGallery } from 'src/components/ui/ImageGallery'
import { Skeleton } from '@acctglobal/skeleton'
import { OrderImagesByRegex } from 'src/utils/OrderImagesByRegex'
import { isNewPDP } from 'src/utils/isNewPDP'

import ProductTags from './ProductTags'
import type { ArrayRequest } from './ProductDetails'

import './product-details.scss'

interface ProductDetailsReturnProps {
  viewBreadcrumb: boolean
  formattedBreadcrumb?: Array<{
    item: string
    name: string
    position: number
  }>
  collectionId?: string
  isNewRelease?: boolean
  productInfo: ArrayRequest | undefined
  hasPolarizedLens?: boolean
  images: Array<{
    url: string
    alternateName: string
  }>
  isSaveProductModalOpen: boolean
  product: BrowserProductQueryQuery['product']
  setIsSaveProductModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  visible: boolean
  isCreateWishlistModalOpen: boolean
  setIsCreateWishlistModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  createWishlist: (name: string, isDefault?: boolean) => Promise<boolean>
  width: 365 | 864
  height: 369.39 | 520
  renderVivaraYou?: boolean
  clusterHighlights: Array<{
    id: string | null
    name: string | null
  }> | null
  productClusters: Array<{
    id: string | null
    name: string | null
  }> | null
}

const ProductDetailsReturn = ({
  viewBreadcrumb,
  formattedBreadcrumb,
  productInfo,
  hasPolarizedLens,
  images,
  isSaveProductModalOpen,
  product,
  setIsSaveProductModalOpen,
  visible,
  isCreateWishlistModalOpen,
  setIsCreateWishlistModalOpen,
  createWishlist,
  width,
  height,
  clusterHighlights,
  productClusters,
  collectionId,
  isNewRelease,
}: ProductDetailsReturnProps) => {
  const {
    person,
    currency: { code },
  } = useSession()

  const { sendProductAbTestEvent } = useProductAbTestEvent()
  const { removeProductFromWishlist } = useWishlistContext()
  const { sendRemoveFromWishlistEvent } = useRemoveFromWishlist()
  const { stampsData } = useStampsDataContext()
  const uuidToUse = getCookie('uuid')

  const selectedSKU = useSelectedSKU(
    product
  ) as BrowserProductQueryQuery['product']

  const productToWishlist = selectedSKU ? { ...selectedSKU } : product

  const skuId = productToWishlist?.sku

  const productIdMatch = product?.slug?.match(/\b[a-z]{2}\d{8}\b/)

  const productId = productIdMatch ? productIdMatch[0]?.toUpperCase() : productToWishlist?.gtin

  const productName = product?.name

  const productPrice = product?.offers?.lowPrice

  const productDiscount = ((product?.offers?.offers?.[0]?.listPrice - product?.offers?.offers?.[0]?.price) * 1)

  const productType = product?.name?.match(/^\p{L}+/u)?.[0] ?? ""

  const productWeight = product?.name?.match(/(\d+)\s*k/i)?.[0] ?? ""

  const productBrand = product?.brand?.name

  const productReference = productToWishlist?.gtin
    
  const categories = getCategorie(
    product?.breadcrumbList?.itemListElement?.map((category) => category),
    productName
  )

  const productCollection =
    String(
      product?.isVariantOf?.additionalProperty?.find(
        (property) => property?.name?.toLowerCase() === 'coleção'
      )?.value ?? ''
    ) || ''

  const productMaterial =
    String(
      product?.isVariantOf?.additionalProperty?.find(
        (property) => property?.name?.toLowerCase() === 'material'
      )?.value ?? ''
    ) || ''

  const productSize = getItemSizeFromProperties(
    product?.isVariantOf?.additionalProperty,
    productToWishlist?.additionalProperty
  )

  const productStone =
    String(
      product?.isVariantOf?.additionalProperty?.find(
        (property) => property?.name?.toLowerCase() === 'pedras'.toLowerCase()
      )?.value ?? ''
    ) || ''

  const productAudience =
    String(
      product?.isVariantOf?.additionalProperty?.find(
        (property) => property?.name?.toLowerCase() === 'sugestão'.toLowerCase()
      )?.value ?? ''
    ) || ''

  const eventData = {
    sku_id: skuId,
    item_id: productId,
    item_name: productName,
    item_price: productPrice,
    item_type: productType,
    item_weight: productWeight,
    item_brand: productBrand,
    item_collection: productCollection,
    item_reference: productReference,
    item_material: productMaterial,
    item_category: categories[0] ?? "",
    item_category2: categories[1] ?? "",
    item_category3: categories[2] ?? "",
    item_category4: categories[3] ?? "",
    item_category5: categories[4] ?? "",
    item_category6: categories[5] ?? "",
    item_currency: 'BRL',
    item_size: productSize ?? "",
    item_stone: productStone,
    item_audience: productAudience,
    quantity: 1,
    discount: productDiscount,
    currency: code as CurrencyCode,
    value: Number(productPrice.toFixed(2)),
    user_logged: !!person?.id,
    user_id: person?.id ?? null,
    uuid: uuidToUse
  }

  const sendWishlistSelectItemAbTestEventToAnalytics = () => {
    sendProductAbTestEvent({
      pdp_version: isNewPDP ? 'versao_2' : 'versao_1',
      action: 'click',
      component: 'botao',
      position_interaction: 'esquerda',
      element_name: 'adicionar_a_wishlist',
      section: 'imagem_principal',
      user_id: person?.id ?? null,
      user_logged: !!person?.id,
      uuid: uuidToUse,
    })
  }

  return (
    <>
      <div className="product-details / grid-content grid-section">
        {!viewBreadcrumb ? (
          <Skeleton width={325} height={18} backgroundColor="#F4F4F4" />
        ) : (
          !isNewPDP &&
          formattedBreadcrumb && (
            <div className="product-details__breadcrumb">
              <Breadcrumb breadcrumbList={formattedBreadcrumb} />
            </div>
          )
        )}
        {!viewBreadcrumb ? (
          <Skeleton width={325} height={18} backgroundColor="#F4F4F4" />
        ) : (
          isNewPDP &&
          formattedBreadcrumb && (
            <div
              className={isNewPDP ? `product-details__breadcrumb` : 'doNotShow'}
            >
              <Breadcrumb breadcrumbList={formattedBreadcrumb} />
            </div>
          )
        )}
        <section className="product-details__body product-details__image">
          <section className="product-details__left-section">
            <section className="product-details__image">
              {visible ? (
                <>
                  {collectionId !== undefined && (
                    <ProductTags
                      collections={clusterHighlights}
                      collectionId={collectionId}
                      isNewRelease={isNewRelease}
                    />
                  )}
                  {product && isNewPDP && (
                    <button
                      className="wishlist-button"
                      onClick={sendWishlistSelectItemAbTestEventToAnalytics}
                    >
                      <WishlistHeart
                        products={[productToWishlist]}
                        hasBackgroundColor
                        isOnShelf={false}
                        onFilledHeartClick={async () => {
                          await removeProductFromWishlist([productToWishlist])
                          sendRemoveFromWishlistEvent(eventData)
                        }}
                        onEmptyHeartClick={() => {
                          setIsSaveProductModalOpen(true)
                        }}
                        pdpPath={window.location.pathname}
                      />
                    </button>
                  )}
                  <div className="product-details__ImageGallery">
                    <ImageGallery
                      images={OrderImagesByRegex(images)?.filter(
                        (img) =>
                          !img?.url?.includes('_hover') &&
                          !img?.url?.includes('-mon')
                      )}
                      videos={
                        productInfo ? productInfo?.items?.[0]?.videos : []
                      }
                    />
                    {stampsData && (
                      <StampsProduct
                        allItems={stampsData}
                        collections={productClusters}
                      />
                    )}
                    {hasPolarizedLens && (
                      <div className="polarized-lens-tag">
                        <div className="polarized-lens-tag-content">
                          <p className="polarized-lens-tag-content-text">
                            Lentes Polarizadas
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="product-details__ImageGallery">
                  <Skeleton
                    width={width}
                    height={height}
                    backgroundColor="#F4F4F4"
                  />
                </div>
              )}
            </section>
          </section>
        </section>
      </div>
      {isSaveProductModalOpen && (
        <AddToWishlistModal
          eventData={eventData}
          products={[{ ...productToWishlist }]}
          product={product}
          isOpen={isSaveProductModalOpen}
          setIsOpen={setIsSaveProductModalOpen}
          openWishlistModal={() => setIsCreateWishlistModalOpen(true)}
        />
      )}
      {isCreateWishlistModalOpen && (
        <SingleInputModal
          title="Dê um nome para sua lista"
          btnText="Salvar"
          isOpen={isCreateWishlistModalOpen}
          setIsOpen={setIsCreateWishlistModalOpen}
          modalAction={createWishlist}
          setIsSaveProductModalOpen={setIsSaveProductModalOpen}
          onClose={() => setIsSaveProductModalOpen(true)}
        />
      )}
    </>
  )
}

export default ProductDetailsReturn
